import { safeCast } from '@/helper';

interface IGameCallboardItem {
  readonly callValue: number;
  callName: string;
  callOrder?: number;
  isCalled: boolean;
}

class GameCallboard {
  public readonly items: IGameCallboardItem[] = [];
  public initialCallValue: number;
  public currentCallValue?: number;
  public currentCallNumber?: number;

  public constructor(maxNumberOfCallsPerGame: number, callboardNames?: string[], initialCallValue?: number) {
    this.initialCallValue = initialCallValue !== undefined ? initialCallValue : 1;
    this.initialiseCallboardItems(maxNumberOfCallsPerGame, callboardNames, this.initialCallValue);
  }

  public update(calledValues: (string | number)[], callNumber?: number) {
    const numberOfCalledValues = calledValues.length;
    this.currentCallNumber = callNumber || numberOfCalledValues;
    this.currentCallValue = Number(calledValues[numberOfCalledValues - 1]);
    calledValues.forEach((callValue, index) => {
      const callValuePosition = Number(callValue) - 1;
      this.items[callValuePosition].isCalled = true;

      if (!callNumber) {
        this.items[callValuePosition].callOrder = index + 1;
      } else {
        this.items[callValuePosition].callOrder = callNumber;
      }
    });
  }

  public reset() {
    this.currentCallValue = null;
    this.currentCallNumber = null;
    this.items.forEach((item) => {
      item.isCalled = false;
    });
  }

  private initialiseCallboardItems(maxNumberOfCallsPerGame: number, callboardNames?: string[], initialCallValue?: number) {
    maxNumberOfCallsPerGame = initialCallValue === 0 ? maxNumberOfCallsPerGame - 1 : maxNumberOfCallsPerGame;

    for (let callValue = initialCallValue; callValue <= maxNumberOfCallsPerGame; callValue++) {
      this.items.push({
        callValue,
        callName: callboardNames ? callboardNames[callValue - 1] : null,
        isCalled: false
      });
    }
  }
}

interface IWordsearchGameCallboardItem {
  readonly callValue: number;
  callWord: string;
  callOrder?: number;
  isCalled: boolean;
}

class WordsearchGameCallBoard {
  public readonly items: IWordsearchGameCallboardItem[] = [];
  public initialCallValue: number;
  public currentCallValue?: number;
  public currentCallNumber?: number;
  public currentCallWord?: string;

  public constructor(maxNumberOfCallsPerGame: number) {
    this.initialiseCallboardItems(maxNumberOfCallsPerGame, 1);
  }


  public catchup(calledValues: (string)[], callNumber?: number) {
    const numberOfCalledValues = calledValues.length;
    this.currentCallNumber = callNumber || numberOfCalledValues;
    this.currentCallValue = Number(calledValues[numberOfCalledValues - 1]);
    this.currentCallWord = String(calledValues[numberOfCalledValues - 1]);
    calledValues.forEach((callValue, index) => {
      this.items.forEach((item) => {
        if (item.callWord === callValue) {
          item.isCalled = true;
          item.callOrder = index + 1;
        }
      });
    });
  }
  // uses index from call value (like other games)
  public update(calledValues: (string | number)[], calledWords: (string)[], callNumber?: number) {
    const numberOfCalledValues = calledValues.length;
    this.currentCallNumber = callNumber || numberOfCalledValues;
    this.currentCallValue = Number(calledValues[numberOfCalledValues - 1]);
    this.currentCallWord = String(calledWords[numberOfCalledValues - 1]);
    calledValues.forEach((callValue, index) => {
      const callValuePosition = Number(callValue) - 1;
      this.items[callValuePosition].isCalled = true;

      if (!callNumber) {
        this.items[callValuePosition].callOrder = index + 1;
      } else {
        this.items[callValuePosition].callOrder = callNumber;
      }
    });
  }

  public reset() {
    this.currentCallValue = null;
    this.currentCallNumber = null;
    this.items.forEach((item) => {
      item.isCalled = false;
    });
  }

  public setCallWords(words: string[]) {
    for (let index = 0; index < words.length; index++) {
      this.items[index].callWord = words[index];
    }
  }

  private initialiseCallboardItems(maxNumberOfCallsPerGame: number, initialCallValue?: number) {
    maxNumberOfCallsPerGame = initialCallValue === 0 ? maxNumberOfCallsPerGame - 1 : maxNumberOfCallsPerGame;

    for (let callValue = initialCallValue; callValue <= maxNumberOfCallsPerGame; callValue++) {
      this.items.push({
        callValue,
        callWord: null,
        isCalled: false
      });
    }
  }
}


interface IBoxNumberCallboardItem {
  readonly callValue: number;
  displayValue: number;
  isCalled: boolean;
  isPlayerBox: boolean;
}

interface IBoxNumberCallboardBox {
  readonly callValue: number;
  isCalled: boolean;
  isPlayerBox: boolean;
}

interface IBoxNumberCallboardPrize {
  readonly callValue: number;
  isCalled: boolean;
}

class BoxNumberCallboard {
  public items: IBoxNumberCallboardItem[] = [];
  private hadFirstCall: boolean;

  public boxes: IBoxNumberCallboardBox[] = [];
  public prizes: IBoxNumberCallboardPrize[] = [];
  public currentCallIsBox: boolean;
  public curretnCallPrize: number;
  public currentCallIsPlayerBox: boolean;

  public update(calledBoxes: number | string, calledPrizes: number | string, callIsBox: boolean, callIsFirstBox: boolean) {
    this.currentCallIsBox = callIsBox;
    this.currentCallIsPlayerBox = callIsFirstBox;

    const box: number = typeof (calledBoxes) === 'string' ? safeCast(calledBoxes, 0) : calledBoxes;
    const prize: number = typeof (calledPrizes) === 'string' ? safeCast(calledPrizes, 0) : calledPrizes;

    this.curretnCallPrize = prize;

    this.boxes.every((i) => {
      if (i.callValue === box && !i.isCalled) {
        i.isCalled = true;
        i.isPlayerBox = callIsFirstBox;
        return false;
      }

      return true;
    });

    this.prizes.every((i) => {
      if (i.callValue === prize && !callIsFirstBox && !i.isCalled) {
        i.isCalled = true;
        return false;
      }

      return true;
    });
  }


  public catchup(calledBoxes: (number | string)[], calledPrizes: (number | string)[], playerBox: number) {
    this.currentCallIsBox = false;
    this.curretnCallPrize = 0;
    calledBoxes.forEach((calledBox) => {
      this.boxes.every((i) => {
        const checkValue = typeof (calledBox) === 'string' ? safeCast(calledBox, 0) : calledBox;
        if (i.callValue === checkValue) {
          i.isCalled = true;

          if (i.callValue === playerBox) {
            i.isPlayerBox = true;
          }

          return false;
        }

        return true;
      });
    });

    calledPrizes.forEach((calledPrize) => {
      this.prizes.every((i) => {
        const checkValue = typeof (calledPrize) === 'string' ? safeCast(calledPrize, 0) : calledPrize;
        if (i.callValue === checkValue) {
          i.isCalled = true;
          return false;
        }

        return true;
      });
    });
  }

  public set(boxes: number[], prize: number[]) {
    this.boxes = [];
    this.prizes = [];

    boxes.forEach((b) => {
      this.boxes.push({
        callValue: b,
        isCalled: false,
        isPlayerBox: false,
      });
    });

    prize.forEach((p) => {
      this.prizes.push({
        callValue: p,
        isCalled: false
      });
    });
  }

  public setItems(initialValues: Record<string, number>) {
    this.items = [];
    this.hadFirstCall = false;

    if (initialValues.length !== 0) {
      for (const key in initialValues) {
        if (initialValues.hasOwnProperty(key)) {
          this.items.push({
            callValue: safeCast(key, 0),
            displayValue: initialValues[key],
            isCalled: false,
            isPlayerBox: false,
          });
        }
      }
    }
  }
}

export { IGameCallboardItem, GameCallboard, IWordsearchGameCallboardItem, WordsearchGameCallBoard, IBoxNumberCallboardItem, IBoxNumberCallboardBox, IBoxNumberCallboardPrize, BoxNumberCallboard };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/_domain/models/game-callboard.ts